/**
 * Bot Page - Market Intelligence
 *
 * Simplified version focused on:
 * - Fear & Greed Index (free API)
 * - Simple trading signals based on portfolio
 * - Basic sentiment overview
 * - Market cycle indicators
 *
 * Removed (Option A simplification):
 * - Complex backtesting
 * - AI Explanations (paid Anthropic API)
 * - Multi-channel notifications
 * - Sentiment Heatmap Calendar (was using mock data)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Icons } from '../components/Icons';
import { PortfolioManager } from '../services/portfolioManager';
import { useCoinPrices } from '../hooks/queries/useCoinGecko';
import { CalculatorFactory } from '../services/calculator';
import { AdviceBotAPI } from '../services/adviceBotAPI';
import { logger } from '../services/logger';
import type { Token, FIFOResults } from '../types';
import { CryptoMarketCycleIndicators } from '../components/visualizations/CryptoMarketCycleIndicators';
import { SignalDetailsModal } from '../components/modals/SignalDetailsModal';

// Refactored components and hooks
import { SignalCard, KPICards, SentimentPanel, SignalFilters } from '../components/bot';
import type { SentimentData } from '../components/bot';
import { normalizeTimeframe } from '../hooks/useTradingSignals';
import type { TradingSignal, SignalType } from '../hooks/useTradingSignals';

type BotProfile = 'conservative' | 'balanced' | 'aggressive' | 'custom';

interface BotConfig {
  profile: BotProfile;
  maxPositionSize: number;
  stopLossPercent: number;
  takeProfitPercent: number;
  riskTolerance: number;
  tradingFrequency: 'low' | 'medium' | 'high';
  enableSentiment: boolean;
  enableTechnical: boolean;
}

export function Bot() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState<BotProfile>('balanced');
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Signal filters
  const [filterAction, setFilterAction] = useState<SignalType | 'all'>('all');
  const [filterConfidence, setFilterConfidence] = useState<number>(0);
  const [filterToken, setFilterToken] = useState<string>('');
  const [filterTimeframe, setFilterTimeframe] = useState<string>('all');

  // Signal details modal
  const [selectedSignal, setSelectedSignal] = useState<TradingSignal | null>(null);
  const [showSignalModal, setShowSignalModal] = useState(false);
  const [modalTechnicalIndicators, setModalTechnicalIndicators] = useState<any>(null);
  const [modalSentiment, setModalSentiment] = useState<any>(null);

  // Backend integration states
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [realSentimentData, setRealSentimentData] = useState<SentimentData[]>([]);
  const [realTradingSignals, setRealTradingSignals] = useState<TradingSignal[]>([]);
  const [usingMockData, setUsingMockData] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Sentiment Analysis states
  const [selectedSentimentToken, setSelectedSentimentToken] = useState<string>('');
  const [sentimentByToken, setSentimentByToken] = useState<Record<string, SentimentData[]>>({});

  const [config, setConfig] = useState<BotConfig>({
    profile: 'balanced',
    maxPositionSize: 20,
    stopLossPercent: 10,
    takeProfitPercent: 25,
    riskTolerance: 5,
    tradingFrequency: 'medium',
    enableSentiment: true,
    enableTechnical: true
  });

  // React Query: Get token IDs for price fetching
  const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);

  // React Query: Fetch real-time prices with auto-refresh (60s)
  const {
    data: prices = {},
    isLoading: pricesLoading,
  } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
    refetchInterval: 60000
  });

  // Load portfolio tokens
  useEffect(() => {
    const loadTokens = async () => {
      setLoading(true);
      const active = PortfolioManager.getActivePortfolio();
      if (active) {
        setTokens(active.tokens);
      }
      setLoading(false);
    };
    loadTokens();
  }, []);

  // Check backend availability and load real data
  useEffect(() => {
    const checkBackendAndLoadData = async () => {
      logger.info('Bot', '🔍 Checking Advice Bot backend...');

      const isAvailable = await AdviceBotAPI.healthCheck();
      setBackendAvailable(isAvailable);

      if (!isAvailable) {
        logger.warn('Bot', '⚠️ Backend not available, using mock data');
        setUsingMockData(true);
        return;
      }

      logger.info('Bot', '✅ Backend available, loading real data...');
      setUsingMockData(false);

      // Load real sentiment data
      if (tokens.length > 0) {
        if (!selectedSentimentToken) {
          setSelectedSentimentToken(tokens[0].id);
        }

        const sentimentMap: Record<string, SentimentData[]> = {};
        const tokensToLoad = tokens.slice(0, 5);

        const sentimentPromises = tokensToLoad.map(async (token) => {
          try {
            const sentiment = await AdviceBotAPI.getAggregatedSentiment(token.id);
            return { token, sentiment };
          } catch (error) {
            logger.error('Bot', `Failed to load sentiment for ${token.id}:`, error);
            return null;
          }
        });

        const sentiments = await Promise.allSettled(sentimentPromises);

        sentiments.forEach((result) => {
          if (result.status === 'fulfilled' && result.value && result.value.sentiment) {
            const { token, sentiment } = result.value;
            const mapped = AdviceBotAPI.mapToSentimentData(sentiment);
            sentimentMap[token.id] = mapped;
          }
        });

        setSentimentByToken(sentimentMap);

        if (sentimentMap[tokens[0].id]) {
          setRealSentimentData(sentimentMap[tokens[0].id]);
        }
      }

      // Load real trading signals
      if (tokens.length > 0 && Object.keys(prices).length > 0) {
        const symbolsWithPrices = tokens.slice(0, 5).map(token => ({
          symbol: token.id,
          price: prices[token.id]?.usd || 0
        }));

        const signals = await AdviceBotAPI.getTradingSignals(
          symbolsWithPrices,
          {
            enableSentiment: config.enableSentiment,
            enableTechnical: config.enableTechnical,
            riskTolerance: config.riskTolerance
          }
        );

        if (signals.length > 0) {
          setRealTradingSignals(signals);
        }
      }
    };

    if (tokens.length > 0 && Object.keys(prices).length > 0) {
      checkBackendAndLoadData();
    }
  }, [tokens, prices, config.enableSentiment, config.enableTechnical, config.riskTolerance]);

  // Calculate FIFO results
  const fifoResults: FIFOResults = useMemo(() => {
    const results: FIFOResults = {};
    tokens.forEach(token => {
      results[token.id] = CalculatorFactory.calculate(token.positions, 'FIFO');
    });
    return results;
  }, [tokens]);

  // Calculate portfolio metrics
  const portfolioMetrics = useMemo(() => {
    const totalValue = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      const price = prices[token.id]?.usd || 0;
      return sum + (fifo.remainingQty || 0) * price;
    }, 0);

    const totalPL = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      const price = prices[token.id]?.usd || 0;
      const value = (fifo.remainingQty || 0) * price;
      const cost = fifo.totalCost || 0;
      return sum + (value - cost);
    }, 0);

    const plPercent = totalValue > 0 ? (totalPL / (totalValue - totalPL)) * 100 : 0;

    return { totalValue, totalPL, plPercent };
  }, [tokens, prices, fifoResults]);


  // Generate portfolio-based trading signals (fallback when backend unavailable)
  const tradingSignals = useMemo(() => {
    if (tokens.length === 0) return [];

    const signals: TradingSignal[] = [];

    tokens.slice(0, 5).forEach(token => {
      const price = prices[token.id]?.usd || 0;
      const fifo = fifoResults[token.id] || {};
      const plPercent = fifo.totalCost && fifo.totalCost > 0
        ? ((fifo.remainingQty || 0) * price - fifo.totalCost) / fifo.totalCost * 100
        : 0;

      let action: SignalType = 'hold';
      let confidence = 65;
      let reason = 'Technical indicators neutral';

      if (plPercent > 25) {
        action = 'sell';
        confidence = 78;
        reason = 'Take profit - target reached';
      } else if (plPercent < -8) {
        action = 'buy';
        confidence = 72;
        reason = 'Oversold - accumulation opportunity';
      }

      signals.push({
        tokenId: token.id,
        tokenName: token.displayName || token.id,
        action,
        confidence,
        reason,
        currentPrice: price,
        timeframe: action === 'hold' ? '1-2 weeks' : '24-48h'
      });
    });

    return signals;
  }, [tokens, prices, fifoResults]);

  // Use real signals if available
  const finalTradingSignals = useMemo(() => {
    if (!usingMockData && realTradingSignals.length > 0) {
      return realTradingSignals;
    }
    return tradingSignals;
  }, [usingMockData, realTradingSignals, tradingSignals]);

  // Apply filters
  const filteredSignals = useMemo(() => {
    return finalTradingSignals.filter(signal => {
      if (filterAction !== 'all' && signal.action !== filterAction) return false;
      if (signal.confidence < filterConfidence) return false;
      if (filterToken && !signal.tokenName.toLowerCase().includes(filterToken.toLowerCase())) return false;
      if (filterTimeframe !== 'all' && signal.timeframe !== filterTimeframe) return false;
      return true;
    });
  }, [finalTradingSignals, filterAction, filterConfidence, filterToken, filterTimeframe]);

  const resetFilters = () => {
    setFilterAction('all');
    setFilterConfidence(0);
    setFilterToken('');
    setFilterTimeframe('all');
  };

  // Open signal details modal
  const openSignalDetails = async (signal: TradingSignal) => {
    setSelectedSignal(signal);
    setShowSignalModal(true);
    setModalTechnicalIndicators(null);
    setModalSentiment(null);

    if (backendAvailable) {
      try {
        const apiTimeframe = normalizeTimeframe(signal.timeframe);
        const [technical, sentiment] = await Promise.all([
          AdviceBotAPI.getTechnicalIndicators(signal.tokenId, apiTimeframe),
          AdviceBotAPI.getAggregatedSentiment(signal.tokenId)
        ]);

        if (technical) setModalTechnicalIndicators(technical);
        if (sentiment) setModalSentiment(sentiment);
      } catch (error) {
        logger.error('Bot', 'Error loading modal data:', error);
      }
    }
  };

  // Manual refresh
  const refreshAllTokens = async () => {
    if (tokens.length === 0) return;

    setIsRefreshing(true);
    logger.info('Bot', '🔄 Manual refresh triggered');

    try {
      // Reload sentiment
      const sentimentMap: Record<string, SentimentData[]> = {};
      const sentimentPromises = tokens.slice(0, 5).map(async (token) => {
        try {
          const sentiment = await AdviceBotAPI.getAggregatedSentiment(token.id);
          return { token, sentiment };
        } catch {
          return null;
        }
      });

      const sentiments = await Promise.allSettled(sentimentPromises);
      sentiments.forEach((result) => {
        if (result.status === 'fulfilled' && result.value && result.value.sentiment) {
          const { token, sentiment } = result.value;
          const mapped = AdviceBotAPI.mapToSentimentData(sentiment);
          sentimentMap[token.id] = mapped;
        }
      });

      setSentimentByToken(sentimentMap);
      if (sentimentMap[tokens[0]?.id]) {
        setRealSentimentData(sentimentMap[tokens[0].id]);
      }

      // Reload signals
      if (Object.keys(prices).length > 0) {
        const symbolsWithPrices = tokens.slice(0, 5).map(token => ({
          symbol: token.id,
          price: prices[token.id]?.usd || 0
        }));

        const signals = await AdviceBotAPI.getTradingSignals(symbolsWithPrices, {
          enableSentiment: config.enableSentiment,
          enableTechnical: config.enableTechnical,
          riskTolerance: config.riskTolerance
        });

        if (signals.length > 0) {
          setRealTradingSignals(signals);
        }
      }
    } catch (error) {
      logger.error('Bot', 'Manual refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Mock sentiment data (fallback)
  const mockSentimentData = useMemo<SentimentData[]>(() => [
    { source: 'Twitter/X', sentiment: 'bullish', score: 72, mentions: 45600, trend: 'up' },
    { source: 'Reddit', sentiment: 'bullish', score: 65, mentions: 12400, trend: 'up' },
    { source: 'News Media', sentiment: 'neutral', score: 8, mentions: 340, trend: 'stable' }
  ], []);

  const sentimentData = useMemo<SentimentData[]>(() => {
    if (!usingMockData && Object.keys(sentimentByToken).length > 0) {
      const tokenToUse = selectedSentimentToken || tokens[0]?.id;
      if (tokenToUse && sentimentByToken[tokenToUse]) {
        return sentimentByToken[tokenToUse];
      }
    }
    return mockSentimentData;
  }, [usingMockData, sentimentByToken, selectedSentimentToken, tokens, mockSentimentData]);

  // Profile configurations
  const profileConfigs = useMemo<Record<BotProfile, Partial<BotConfig>>>(() => ({
    conservative: {
      maxPositionSize: 10,
      stopLossPercent: 5,
      takeProfitPercent: 15,
      riskTolerance: 3,
      tradingFrequency: 'low'
    },
    balanced: {
      maxPositionSize: 20,
      stopLossPercent: 10,
      takeProfitPercent: 25,
      riskTolerance: 5,
      tradingFrequency: 'medium'
    },
    aggressive: {
      maxPositionSize: 35,
      stopLossPercent: 15,
      takeProfitPercent: 50,
      riskTolerance: 8,
      tradingFrequency: 'high'
    },
    custom: config
  }), [config]);

  const applyProfile = useCallback((profile: BotProfile) => {
    setSelectedProfile(profile);
    if (profile !== 'custom') {
      setConfig(prev => ({ ...prev, ...profileConfigs[profile], profile }));
    }
  }, [profileConfigs]);

  // KPI data - real data only
  const signalsSparkline = useMemo(() =>
    Array.from({ length: 7 }, () => finalTradingSignals.length),
    [finalTradingSignals.length]
  );
  const portfolioSparklineData = useMemo(() => [], []);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div>Loading market data...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Honest disclaimer banner — paper / informational only */}
      <div
        role="status"
        aria-live="polite"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem',
          background: 'rgba(245, 158, 11, 0.12)',
          border: '1px solid rgba(245, 158, 11, 0.5)',
          borderRadius: '12px',
          padding: '0.75rem 1.25rem',
          marginBottom: '24px',
          fontSize: '0.9rem',
          color: '#f59e0b',
          fontWeight: 500,
        }}
      >
        <span style={{ fontSize: '1.25rem', flexShrink: 0, lineHeight: 1.2 }} aria-hidden="true">⚠️</span>
        <span>
          <strong>Outil informationnel — pas d'exécution réelle.</strong> Les signaux ci-dessous sont générés
          à partir d'indicateurs publics (Fear &amp; Greed, prix, sentiment). Aucun ordre n'est passé sur tes
          exchanges. La connexion exchanges réelle arrive en Phase 3 de la roadmap.
        </span>
      </div>

      {/* Header */}
      <div style={{
        marginBottom: '40px',
        padding: '40px',
        borderRadius: '16px',
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%)',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Backend Status Badge */}
        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{
            padding: '6px 14px',
            borderRadius: '20px',
            background: backendAvailable ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
            border: `1px solid ${backendAvailable ? 'rgba(16, 185, 129, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            fontSize: '12px',
            fontWeight: 700,
            color: backendAvailable ? '#10b981' : '#ef4444',
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span style={{
              width: '6px',
              height: '6px',
              borderRadius: '50%',
              background: backendAvailable ? '#10b981' : '#ef4444'
            }} />
            {backendAvailable ? '🟢 ONLINE' : '🔴 OFFLINE'}
          </div>

          {backendAvailable && tokens.length > 0 && (
            <button
              onClick={refreshAllTokens}
              disabled={isRefreshing}
              style={{
                padding: '6px 14px',
                borderRadius: '20px',
                background: 'rgba(139, 92, 246, 0.2)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                fontSize: '12px',
                fontWeight: 700,
                color: '#8b5cf6',
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
                opacity: isRefreshing ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Icons.RefreshCw size={12} style={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </button>
          )}
        </div>

        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{
            fontSize: '42px',
            fontWeight: 800,
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            background: 'linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            <Icons.Sparkles size={42} style={{ color: '#8b5cf6' }} />
            Market Intelligence
          </h1>

          <p style={{
            fontSize: '18px',
            color: 'var(--muted)',
            marginBottom: '24px',
            maxWidth: '700px',
            lineHeight: '1.6'
          }}>
            Trading signals, sentiment analysis, and market indicators for your portfolio
          </p>
        </div>
      </div>

      {/* Bot Status */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '24px' }}>
          <div style={{ flex: 1, minWidth: '250px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: '#10b981',
                boxShadow: '0 0 12px #10b981'
              }} />
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '2px' }}>
                  Market Intelligence Active
                </h2>
                <p style={{ fontSize: '13px', color: 'var(--muted)', margin: 0 }}>
                  Profile: <strong style={{ color: 'var(--accent)' }}>{selectedProfile.charAt(0).toUpperCase() + selectedProfile.slice(1)}</strong>
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' }}>
              <div style={{
                padding: '4px 10px',
                borderRadius: '12px',
                background: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.2)',
                fontSize: '12px',
                fontWeight: 600,
                color: '#8b5cf6'
              }}>
                {filteredSignals.length} signals
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="btn-ghost"
              onClick={() => setShowConfigModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px' }}
            >
              <Icons.Settings size={18} />
              Configure
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <KPICards
        portfolioMetrics={portfolioMetrics}
        portfolioSparklineData={portfolioSparklineData}
        signalsSparkline={signalsSparkline}
        sparklineLoading={false}
        tradingSignals={finalTradingSignals}
      />

      {/* Trading Signals */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', margin: 0 }}>
            <Icons.TrendingUp size={20} />
            Trading Signals
            <span style={{
              padding: '2px 8px',
              borderRadius: '10px',
              background: 'rgba(139, 92, 246, 0.15)',
              fontSize: '12px',
              fontWeight: 600,
              color: 'var(--accent)'
            }}>
              {filteredSignals.length}
            </span>
          </h2>
        </div>

        {/* Status Info */}
        {!backendAvailable && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#f59e0b'
          }}>
            <strong>Mode Démonstration:</strong> Signaux basés sur votre portfolio. Backend API disponible pour données en temps réel.
          </div>
        )}

        {backendAvailable && !usingMockData && (
          <div style={{
            padding: '12px 16px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px',
            marginBottom: '16px',
            fontSize: '13px',
            color: '#10b981'
          }}>
            <strong>✅ API Connectée:</strong> Signaux basés sur l'analyse sentiment + technique en temps réel.
          </div>
        )}

        {/* Filters */}
        <SignalFilters
          filterAction={filterAction}
          filterConfidence={filterConfidence}
          filterToken={filterToken}
          filterTimeframe={filterTimeframe}
          onActionChange={setFilterAction}
          onConfidenceChange={setFilterConfidence}
          onTokenChange={setFilterToken}
          onTimeframeChange={setFilterTimeframe}
          onReset={resetFilters}
        />

        {/* Signals List */}
        <div style={{ display: 'grid', gap: '12px' }}>
          {tokens.length === 0 ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: 'var(--muted)',
              background: 'var(--bg)',
              borderRadius: '12px'
            }}>
              <Icons.Wallet size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <div style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px' }}>
                Aucun Token dans votre Portfolio
              </div>
              <div style={{ fontSize: '14px', marginBottom: '20px' }}>
                Ajoutez des tokens pour recevoir des signaux personnalisés.
              </div>
              <button
                onClick={() => window.location.href = '/portfolio'}
                className="btn"
                style={{
                  padding: '10px 24px',
                  background: 'var(--accent)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 600
                }}
              >
                <Icons.Plus size={16} /> Ajouter des Tokens
              </button>
            </div>
          ) : filteredSignals.length === 0 ? (
            <div style={{
              padding: '40px',
              textAlign: 'center',
              color: 'var(--muted)',
              background: 'var(--bg)',
              borderRadius: '12px'
            }}>
              <Icons.Filter size={32} style={{ margin: '0 auto 12px', opacity: 0.5 }} />
              <div style={{ fontSize: '16px', fontWeight: 600 }}>Aucun signal ne correspond aux filtres</div>
            </div>
          ) : (
            filteredSignals.map((signal, index) => (
              <SignalCard
                key={index}
                signal={signal}
                onOpenDetails={openSignalDetails}
                onRunBacktest={() => {}} // Disabled
                onExplainSignal={() => {}} // Disabled
                backendAvailable={false} // Hide backtest/explain buttons
                backtestLoading={false}
                explanationLoading={false}
              />
            ))
          )}
        </div>
      </div>

      {/* Sentiment Analysis */}
      <SentimentPanel
        tokens={tokens}
        selectedToken={selectedSentimentToken}
        onSelectToken={setSelectedSentimentToken}
        sentimentData={sentimentData}
        sentimentByToken={sentimentByToken}
        usingMockData={usingMockData}
      />

      {/* Market Cycle Indicators (Fear & Greed) */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <Icons.Activity size={24} style={{ color: 'var(--accent)' }} />
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '4px' }}>
              Market Cycle Indicators
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--muted)', margin: 0 }}>
              Fear & Greed Index and market sentiment metrics
            </p>
          </div>
        </div>

        <CryptoMarketCycleIndicators symbol={selectedSentimentToken || tokens[0]?.id || 'BTC'} />
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}
        onClick={() => setShowConfigModal(false)}
        >
          <div className="card" style={{
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
                <Icons.Settings size={22} /> Configuration
              </h2>
              <button
                onClick={() => setShowConfigModal(false)}
                className="btn-ghost"
                style={{ padding: '8px', minWidth: 'auto' }}
              >
                <Icons.X size={20} />
              </button>
            </div>

            {/* Profile Selector */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px', display: 'block', fontWeight: 600 }}>
                Trading Profile
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                {(['conservative', 'balanced', 'aggressive', 'custom'] as BotProfile[]).map(profile => (
                  <button
                    key={profile}
                    onClick={() => applyProfile(profile)}
                    className={selectedProfile === profile ? 'btn-primary' : 'btn-ghost'}
                    style={{ padding: '12px', fontSize: '14px', fontWeight: 600, textTransform: 'capitalize' }}
                  >
                    {profile}
                  </button>
                ))}
              </div>
            </div>

            {/* Sliders */}
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '8px', display: 'block', fontWeight: 600 }}>
                  Risk Tolerance: <strong>{config.riskTolerance}/10</strong>
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={config.riskTolerance}
                  onChange={(e) => setConfig(prev => ({ ...prev, riskTolerance: parseInt(e.target.value), profile: 'custom' }))}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '8px', display: 'block', fontWeight: 600 }}>
                  Stop Loss: <strong>{config.stopLossPercent}%</strong>
                </label>
                <input
                  type="range"
                  min="3"
                  max="25"
                  value={config.stopLossPercent}
                  onChange={(e) => setConfig(prev => ({ ...prev, stopLossPercent: parseInt(e.target.value), profile: 'custom' }))}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '8px', display: 'block', fontWeight: 600 }}>
                  Take Profit: <strong>{config.takeProfitPercent}%</strong>
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={config.takeProfitPercent}
                  onChange={(e) => setConfig(prev => ({ ...prev, takeProfitPercent: parseInt(e.target.value), profile: 'custom' }))}
                  style={{ width: '100%', cursor: 'pointer' }}
                />
              </div>

              {/* Analysis Toggles */}
              <div style={{ paddingTop: '12px', borderTop: '1px solid var(--border)' }}>
                <label style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '12px', display: 'block', fontWeight: 600 }}>
                  Analysis Methods
                </label>
                <div style={{ display: 'flex', gap: '12px', flexDirection: 'column' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.enableSentiment}
                      onChange={(e) => setConfig(prev => ({ ...prev, enableSentiment: e.target.checked }))}
                    />
                    Enable Sentiment Analysis
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={config.enableTechnical}
                      onChange={(e) => setConfig(prev => ({ ...prev, enableTechnical: e.target.checked }))}
                    />
                    Enable Technical Analysis
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid var(--border)' }}>
              <button
                onClick={() => setShowConfigModal(false)}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Save
              </button>
              <button
                onClick={() => setShowConfigModal(false)}
                className="btn-ghost"
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Signal Details Modal */}
      <SignalDetailsModal
        isOpen={showSignalModal}
        onClose={() => setShowSignalModal(false)}
        signal={selectedSignal}
        technicalIndicators={modalTechnicalIndicators}
        sentiment={modalSentiment}
      />

      <p style={{ fontSize: '12px', color: 'var(--muted)', textAlign: 'center', marginTop: '24px' }}>
        Market Intelligence v3.0 - Simplified Edition | Fear & Greed + Sentiment + Signals
      </p>
    </div>
  );
}
